import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  IconButton,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import AppTimePicker from "./AppTimePicker";
import themestyle from "../theme";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { resizeFile } from "../utils/imageResizer";
import { CLASS_TYPE_OPTIONS, DAY_OPTIONS, MESSAGES } from "../utils/const";
import {
  CREATE_CLASS,
  UPDATE_CLASS,
  UPDATE_CLASS_GROUP,
} from "../graphql/mutation";
import {
  ME,
  GET_CLASS_BY_ID,
  GET_INSTRUCTOR_LIST,
  GET_STUDENT_LIST,
  GET_DASHBOARD_COUNTS,
  GET_INSTRUCTOR_ACTIVITIES,
} from "../graphql/query";
import { useMutation, useQuery } from "@apollo/client/react";
import { CLASS_REQUIRED_FIELDS } from "../utils/const";
import {
  CreateClassInput,
  CreateClassResponse,
  UpdateClassInput,
  UpdateClassResponse,
  GetClassByIdResponse,
  SnackbarType,
} from "../interfaces/auth.interface";
import type {
  GetStudentListResponse,
  StudentListItem,
} from "../interfaces/student.interface";
import CustomSnackbar from "../components/Snackbar";
import {
  textFieldSX,
  FormLabel,
  autocompleteListboxSX,
} from "../utils/formField";
import BulkDateSelector from "./BulkDateSelector";
import SchedulePreview from "./SchedulePreview";
import { validateClassField } from "../utils/validation";
import { AddClassFormProps } from "../interfaces/classProps";
import {
  CLASS_MESSAGES,
  CLASS_LABELS,
  CLASS_BUTTONS,
  CLASS_PLACEHOLDERS,
  CLASS_IMAGE_TEXT,
} from "../utils/const";

const MAX_IMAGE_SIZE_MB = 10;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

const AddClassForm: React.FC<AddClassFormProps> = ({
  mode = "add",
  id,
  onSuccess,
  onCancel,
  onEdit,
  onDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addDateOpen, setAddDateOpen] = useState(false);
  const [formData, setFormData] = useState({
    className: "",
    instructorName: "",
    instructorId: "",
    classType: "",
    day: "",
    startTime: "",
    endTime: "",
    description: "",
    coverImage: "",
  });
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [enrolledStudents, setEnrolledStudents] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isImageCleared, setIsImageCleared] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<SnackbarType>("success");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentGlobalDate, setCurrentGlobalDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [isSaving, setIsSaving] = useState(false);

  const isViewMode = mode === "view";
  const isDateEditable = mode === "add";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: userData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const userRole =
    userData?.me?.role?.toUpperCase() || storedUser?.role?.toUpperCase() || "";

  const {
    data: classData,
    loading: contentLoading,
    refetch,
  } = useQuery<GetClassByIdResponse, { id: string }>(GET_CLASS_BY_ID, {
    variables: { id: id || "" },
    skip: mode === "add" || !id,
    fetchPolicy: "network-only",
  });

  const { data: instructorsData } = useQuery(GET_INSTRUCTOR_LIST, {
    variables: { page: 1, perPage: 100 },
    fetchPolicy: "network-only",
  });
  const { data: studentsData } = useQuery<GetStudentListResponse>(
    GET_STUDENT_LIST,
    {
      variables: { page: 1, perPage: 1000, search: "" },
      fetchPolicy: "network-only",
    },
  );

  const instructorOptions = instructorsData?.getInstructorList?.data || [];
  const studentOptions: StudentListItem[] =
    studentsData?.getStudentList?.data || [];

  const [createClass, { loading: createLoading }] = useMutation<
    CreateClassResponse,
    { input: CreateClassInput }
  >(CREATE_CLASS, {
    refetchQueries: [
      { query: GET_DASHBOARD_COUNTS },
      { query: GET_INSTRUCTOR_ACTIVITIES },
    ],
  });

  const [updateClass, { loading: updateLoading }] = useMutation<
    UpdateClassResponse,
    { id: string; input: UpdateClassInput }
  >(UPDATE_CLASS, {
    refetchQueries: [
      { query: GET_DASHBOARD_COUNTS },
      { query: GET_INSTRUCTOR_ACTIVITIES },
    ],
  });

  const [updateClassGroup] = useMutation(UPDATE_CLASS_GROUP);

  const loading = createLoading || updateLoading;

  useEffect(() => {
    const fetchGlobalTime = async () => {
      try {
        const response = await fetch(
          "https://worldtimeapi.org/api/timezone/Etc/UTC",
        );
        const data = await response.json();
        if (data && data.datetime) {
          const globalDate = data.datetime.split("T")[0];
          setCurrentGlobalDate(globalDate);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchGlobalTime();
  }, []);

  useEffect(() => {
    if (classData?.getClassById?.success && classData?.getClassById?.data) {
      const data = classData.getClassById.data;
      setFormData({
        className: data.className || "",
        instructorName: data.instructorName || "",
        instructorId: data.instructorId || "",
        classType: data.classType || "",
        day: data.day || "",
        startTime: data.startTime || "",
        endTime: data.endTime || "",
        description: data.description || "",
        coverImage: data.coverImage || "",
      });
      setEnrolledStudents(data.enrolledStudents || []);
      setSelectedDates(data.day ? [data.day] : []);

      if (data.coverImage) {
        setImagePreview(`data:image/*;base64,${data.coverImage}`);
        setIsImageCleared(false);
      }
    }
  }, [classData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isViewMode) return;
    const { name, value } = e.target;

    // Resolve the start/end pair using the latest edited value so the
    // relational validation below works off a consistent snapshot.
    const start = name === "startTime" ? value : formData.startTime;
    let end = name === "endTime" ? value : formData.endTime;

    // Changing the start time to a value that is not before the existing end
    // time clears the now-invalid end time so the user re-picks it.
    const clearEndTime =
      name === "startTime" && end && toMinutes(start) >= toMinutes(end);
    if (clearEndTime) end = "";

    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };
      if (clearEndTime) updatedFormData.endTime = "";
      return updatedFormData;
    });

    setErrors((prev) => {
      const newErrors = { ...prev, [name]: validateClassField(name, value) };

      if (clearEndTime) {
        // End time was reset, so any stale end-time error no longer applies.
        delete newErrors.endTime;
      } else if (start && end && toMinutes(start) >= toMinutes(end)) {
        // End time is not after the start time -> show the validation message.
        newErrors.endTime = CLASS_MESSAGES.END_TIME_ERROR;
      }

      return newErrors;
    });
  };

  // AppTimePicker emits an "HH:mm" 24h string; funnel it through the same handleChange used
  // by the other fields so the start/end relational validation is preserved.
  const handleTimeChange = (name: "startTime" | "endTime", hhmm: string) => {
    handleChange({
      target: { name, value: hhmm },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  // A *complete* but rejected value (end before start) is reported by the picker via
  // onValidationError ("minTime"/"maxTime"); show the relational message for it.
  const handleTimeError = (name: "startTime" | "endTime", reason: string | null) => {
    if (reason !== "minTime" && reason !== "maxTime") return;
    setErrors((prev) => ({ ...prev, [name]: CLASS_MESSAGES.END_TIME_ERROR }));
  };

  // The masked picker lets the user leave a half-entered time ("13:--") which MUI
  // treats as empty, so neither onChange nor onError fire. We catch it on blur of the
  // field's wrapper (React onBlur bubbles from the inner input) and read the input's
  // value: a digit next to a placeholder dash means an incomplete -> "Invalid time".
  const handleTimeBlur = (
    name: "startTime" | "endTime",
    e: React.FocusEvent<HTMLDivElement>,
  ) => {
    const input = e.currentTarget.querySelector("input");
    const text = input?.value ?? "";
    if (/\d/.test(text) && text.includes("-")) {
      setErrors((prev) => ({ ...prev, [name]: CLASS_MESSAGES.INVALID_TIME }));
    }
  };

  const toMinutes = (time: string) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const getWeekday = (dateString: string) => {
    if (!dateString || !dateString.includes("-")) return dateString || "";
    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date);
  };
  const formatDisplayDate = (dateString: string) => {
    if (!dateString || !dateString.includes("-")) return dateString || "";
    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return dateString;
    const d = date.getDate();
    const m = date.getMonth() + 1;
    const y = date.getFullYear();
    const weekday = getWeekday(dateString);
    return `${d}.${m}.${y} & ${weekday}`;
  };

  const isFormValid = () => {
    for (const field of CLASS_REQUIRED_FIELDS) {
      if (field === "day" || field === "coverImage") continue;
      const value = formData[field as keyof typeof formData];
      if (validateClassField(field, value)) {
        return false;
      }
    }
    if (selectedDates.length === 0) return false;
    if (!imagePreview) return false;
    if (
      formData.startTime &&
      formData.endTime &&
      toMinutes(formData.startTime) >= toMinutes(formData.endTime)
    ) {
      return false;
    }
    if (errors.coverImage) return false;
    return true;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (isViewMode) return;
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        coverImage: CLASS_MESSAGES.INVALID_IMAGE_FORMAT,
      }));
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setErrors((prev) => ({
        ...prev,
        coverImage: CLASS_MESSAGES.IMAGE_SIZE_ERROR,
      }));
      event.target.value = "";
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.coverImage;
      return newErrors;
    });

    try {
      const resizedFile = await resizeFile(file);
      setImageFile(resizedFile);
      setImagePreview(URL.createObjectURL(resizedFile));
      setIsImageCleared(false);
    } catch (err) {
      console.error("Error resizing image:", err);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIsImageCleared(false);
    }
  };
  const handleBrowseClick = () => {
    if (isViewMode) return;
    fileInputRef.current?.click();
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isViewMode) return;
    event.preventDefault();
    setIsDragActive(true);
  };
  const handleDragLeave = () => {
    if (isViewMode) return;
    setIsDragActive(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (isViewMode) return;
    event.preventDefault();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        coverImage: CLASS_MESSAGES.INVALID_IMAGE_FORMAT,
      }));
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setErrors((prev) => ({
        ...prev,
        coverImage: CLASS_MESSAGES.IMAGE_SIZE_ERROR,
      }));
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.coverImage;
      return newErrors;
    });

    try {
      const resizedFile = await resizeFile(file);
      setImageFile(resizedFile);
      setImagePreview(URL.createObjectURL(resizedFile));
      setIsImageCleared(false);
    } catch (err) {
      console.error("Error resizing image:", err);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIsImageCleared(false);
    }
  };

  const handleClearImage = () => {
    if (isViewMode) return;
    if (fileInputRef.current) fileInputRef.current.value = "";
    setImageFile(null);
    setImagePreview(null);
    setIsImageCleared(true);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      if (mode === "add") {
        // Single createClass call with all selected dates as scheduledDates
        const { data } = await createClass({
          variables: {
            input: {
              className: formData.className,
              instructorName: formData.instructorName,
              instructorId: formData.instructorId,
              classType: formData.classType,
              scheduledDates: selectedDates,
              startTime: formData.startTime,
              endTime: formData.endTime,
              enrolledStudents,
              description: formData.description,
              coverImage: imageFile ?? undefined,
            },
          },
        });

        if (!data?.createClass?.success) {
          setSnackbarMessage(data?.createClass?.message || "Failed");
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(
          selectedDates.length > 1
            ? `Class created with ${selectedDates.length} scheduled dates`
            : data.createClass.message,
        );
        setSnackbarType("success");
      } else if (mode === "edit" && id) {
        const { data } = await updateClass({
          variables: {
            id,
            input: {
              className: formData.className,
              instructorName: formData.instructorName,
              instructorId: formData.instructorId,
              classType: formData.classType,
              day: selectedDates[0] || "",
              startTime: formData.startTime,
              endTime: formData.endTime,
              enrolledStudents,
              description: formData.description,
              coverImage: imageFile
                ? imageFile
                : isImageCleared
                  ? null
                  : undefined,
            },
          },
        });

        if (!data?.updateClass?.success) {
          setSnackbarMessage(data?.updateClass?.message || "Failed");
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }

        // Sync enrolled students across all sibling classes (same className+instructorId+startTime+endTime)
        await updateClassGroup({
          variables: {
            className: formData.className,
            instructorId: formData.instructorId,
            startTime: formData.startTime,
            endTime: formData.endTime,
            enrolledStudents,
          },
        });

        setSnackbarMessage(
          data.updateClass.message || "Class updated successfully",
        );
        setSnackbarType("success");
      }
      setSnackbarOpen(true);
      if (onSuccess) {
        setTimeout(onSuccess, 1000);
      } else {
        if (mode === "add") {
          setFormData({
            className: "",
            instructorName: "",
            instructorId: "",
            classType: "",
            day: "",
            startTime: "",
            endTime: "",
            description: "",
            coverImage: "",
          });
          setSelectedDates([]);
          setEnrolledStudents([]);
          setErrors({});
          handleClearImage();
          setIsImageCleared(false);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box
      bgcolor={themestyle.colors.white}
      borderRadius="20px"
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <Box
        sx={{
          backgroundColor: themestyle.colors.lightGreenBg,
          px: 3,
          py: 2.5,
          borderTopLeftRadius: "20px",
          borderTopRightRadius: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          fontSize="20px"
          fontWeight={700}
          color={themestyle.colors.activeGreen}
          fontFamily={themestyle.fontFamily.Roboto}
        >
          {CLASS_MESSAGES.DETAILS_TITLE}
        </Typography>
      </Box>
      <Box
        p={3}
        display="flex"
        flexDirection="column"
        gap={3}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3, md: 5 },
        }}
      >
        <Box display="flex" gap={3} flexDirection={{ xs: "column", md: "row" }}>
          <Box flex={1}>
            <FormLabel>{CLASS_LABELS.CLASS_NAME} *</FormLabel>
            <TextField
              fullWidth
              sx={textFieldSX}
              name="className"
              value={formData.className}
              placeholder={CLASS_PLACEHOLDERS.CLASS_NAME}
              onChange={handleChange}
              error={!!errors.className}
              helperText={errors.className}
              disabled={isViewMode}
            />
          </Box>

          <Box flex={1}>
            <FormLabel>{CLASS_LABELS.INSTRUCTOR} *</FormLabel>
            <Autocomplete
              value={
                instructorOptions.find(
                  (opt: any) => opt.id === formData.instructorId,
                ) ||
                (formData.instructorId || formData.instructorName
                  ? {
                    id: formData.instructorId,
                    fullname: formData.instructorName,
                    instructorId: formData.instructorId,
                  }
                  : null)
              }
              options={instructorOptions}
              getOptionLabel={(option: any) =>
                option.fullname && option.instructorId
                  ? `${option.fullname} - ${option.instructorId}`
                  : option.fullname || ""
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.id === value?.id
              }
              onChange={(_, newValue: any) => {
                if (isViewMode) return;
                setFormData((prev) => ({
                  ...prev,
                  instructorName: newValue?.fullname || "",
                  instructorId: newValue?.id || "",
                }));
                setErrors((prev) => ({
                  ...prev,
                  instructorName: validateClassField(
                    "instructorName",
                    newValue?.fullname || "",
                  ),
                  instructorId: validateClassField(
                    "instructorId",
                    newValue?.id || "",
                  ),
                }));
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex !important",
                    justifyContent: "space-between !important",
                    width: "100%",
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, flexGrow: 1 }}
                  >
                    {option.fullname}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "12px", color: "#6B7280", ml: 1 }}
                  >
                    {option.instructorId}
                  </Typography>
                </Box>
              )}
              slotProps={{
                listbox: { sx: autocompleteListboxSX },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={CLASS_PLACEHOLDERS.INSTRUCTOR}
                  sx={textFieldSX}
                  error={!!errors.instructorName}
                  helperText={errors.instructorName}
                />
              )}
            />
          </Box>
        </Box>
        <Box display="flex" gap={3} flexDirection={{ xs: "column", md: "row" }}>
          <Box flex={1} minWidth={0}>
            <FormLabel>{CLASS_LABELS.CLASS_TYPE} *</FormLabel>
            <Autocomplete
              options={CLASS_TYPE_OPTIONS}
              disabled={isViewMode}
              value={
                CLASS_TYPE_OPTIONS.find(
                  (opt) => opt.value === formData.classType,
                ) || null
              }
              onChange={(_, value) => {
                if (isViewMode) return;
                const val = value?.value || "";
                setFormData((prev) => ({ ...prev, classType: val }));
                setErrors((prev) => ({
                  ...prev,
                  classType: validateClassField("classType", val),
                }));
              }}
              slotProps={{
                listbox: {
                  sx: autocompleteListboxSX,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={CLASS_PLACEHOLDERS.CLASS_TYPE}
                  sx={textFieldSX}
                  error={!!errors.classType}
                  helperText={errors.classType}
                />
              )}
            />
          </Box>
          <Box flex={1} minWidth={0}>
            <Box display="flex" gap={2}>
              <Box flex={1} minWidth={0} onBlur={(e) => handleTimeBlur("startTime", e)}>
                <FormLabel>Start Time *</FormLabel>
                <AppTimePicker
                  value={formData.startTime}
                  onChange={(v) => handleTimeChange("startTime", v)}
                  onValidationError={(r) => handleTimeError("startTime", r)}
                  disabled={isViewMode}
                  error={!!errors.startTime}
                  helperText={errors.startTime}
                />
              </Box>

              <Box flex={1} minWidth={0} onBlur={(e) => handleTimeBlur("endTime", e)}>
                <FormLabel>End Time *</FormLabel>
                <AppTimePicker
                  value={formData.endTime}
                  onChange={(v) => handleTimeChange("endTime", v)}
                  onValidationError={(r) => handleTimeError("endTime", r)}
                  disabled={isViewMode}
                  // End must be after start: block selecting anything up to the start time.
                  minTime={
                    formData.startTime
                      ? dayjs(formData.startTime, "HH:mm").add(1, "minute").format("HH:mm")
                      : undefined
                  }
                  error={!!errors.endTime}
                  helperText={errors.endTime}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        {mode === "add" && (
          <>
            <Box>
              <FormLabel>{CLASS_LABELS.DAY} *</FormLabel>
              <BulkDateSelector
                isViewMode={!isDateEditable}
                onGenerate={(dates: string[]) => {
                  if (!isDateEditable) return;
                  setSelectedDates(() => {
                    const newDates = Array.from(new Set([...dates]));
                    return newDates.sort();
                  });
                  setErrors((prev) => ({ ...prev, day: "" }));
                }}
              />
            </Box>
            <Box sx={{ position: "relative" }}>
              <DesktopDatePicker
                open={addDateOpen}
                onClose={() => setAddDateOpen(false)}
                value={null}
                format="DD-MM-YYYY"
                minDate={currentGlobalDate ? dayjs(currentGlobalDate, "YYYY-MM-DD") : undefined}
                disabled={!isDateEditable}
                onChange={(d) => {
                  if (!isDateEditable || !d || !d.isValid()) return;
                  const dateVal = d.format("YYYY-MM-DD");
                  if (!selectedDates.includes(dateVal)) {
                    setSelectedDates((prev) => [...prev, dateVal].sort());
                  }
                  setErrors((prev) => ({ ...prev, day: "" }));
                  setAddDateOpen(false);
                }}
                // Visually hidden trigger field — the picker is opened from the
                // "Add Date" button below; the field stays in the DOM so the popup anchors here.
                slotProps={{
                  textField: {
                    sx: {
                      position: "absolute",
                      right: { xs: "90%", sm: "16px" },
                      transform: { xs: "translateX(50%)", sm: "none" },
                      top: "40px",
                      opacity: 0,
                      width: "1px",
                      height: "1px",
                      pointerEvents: "none",
                    },
                  },
                }}
              />
              {errors.day && (
                <Typography
                  color="error"
                  sx={{
                    position: "absolute",
                    top: "-24px",
                    right: "0",
                    fontSize: { xs: "10px", sm: "12px" },
                    fontFamily: themestyle.fontFamily.Roboto,
                  }}
                >
                  {errors.day}
                </Typography>
              )}
              <SchedulePreview
                selectedDates={selectedDates}
                onRemoveDate={(date: string) => {
                  setSelectedDates((prev) => prev.filter((d) => d !== date));
                }}
                onAddDate={() => {
                  if (!isDateEditable) return;
                  setAddDateOpen(true);
                }}
                isViewMode={!isDateEditable}
              />
            </Box>
          </>
        )}
        <Box>
          <FormLabel>Enrolled Students</FormLabel>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={studentOptions}
            getOptionLabel={(option) =>
              `${option.studentId || "-"} - ${option.fullName || ""}`
            }
            value={enrolledStudents.map((idOrName) => {
              const found = studentOptions.find(
                (opt) =>
                  opt.studentId === idOrName ||
                  opt.fullName.trim().toLowerCase() ===
                  idOrName.trim().toLowerCase(),
              );
              return (
                found ||
                ({ id: idOrName, fullName: idOrName } as StudentListItem)
              );
            })}
            isOptionEqualToValue={(option, value) =>
              option.studentId && value.studentId
                ? option.studentId === value.studentId
                : option.id === value.id
            }
            onChange={(_, newValue) => {
              if (isViewMode) return;
              const identifiers = newValue
                .map((s) => s.studentId || s.fullName.trim())
                .filter(Boolean);
              setEnrolledStudents(Array.from(new Set(identifiers)));
            }}
            disabled={isViewMode}
            slotProps={{
              listbox: {
                sx: autocompleteListboxSX,
              },
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option.id}-${index}`}
                  label={`${option.studentId || "-"} - ${option.fullName}`}
                  sx={{ bgcolor: "#F3F4F6", fontWeight: 500 }}
                />
              ))
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.studentId || "-"} - {option.fullName}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={textFieldSX}
                placeholder={
                  enrolledStudents.length ? "" : "Select enrolled students"
                }
              />
            )}
          />
        </Box>
        <Box>
          <FormLabel>{CLASS_LABELS.COVER_IMAGE} *</FormLabel>
          <Box
            onClick={handleBrowseClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: `1px dashed ${themestyle.colors.uploadBorder}`,
              borderRadius: "12px",
              p: isMobile ? 1 : 4,
              textAlign: "center",
              cursor: isViewMode ? "default" : "pointer",
              position: "relative",
              bgcolor: isDragActive ? "#EEF7E9" : "#FAFAFA",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
              disabled={isViewMode}
            />
            {imagePreview ? (
              <>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    maxHeight: "180px",
                    maxWidth: "100%",
                    borderRadius: "8px",
                  }}
                />
                {!isViewMode && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearImage();
                    }}
                    sx={{
                      position: "absolute",
                      top: isMobile ? 4 : 8,
                      right: isMobile ? 4 : 8,
                      bgcolor: themestyle.colors.white,
                      padding: isMobile ? "4px" : "8px",
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                  >
                    <CloseIcon
                      sx={{ fontSize: isMobile ? "1.2rem" : "1.5rem" }}
                    />
                  </IconButton>
                )}
              </>
            ) : (
              <>
                <CloudUploadOutlinedIcon
                  sx={{
                    fontSize: isMobile ? 32 : 40,
                    color: themestyle.colors.activeGreen,
                  }}
                />
                {!isViewMode && (
                  <>
                    <Typography
                      mt={1}
                      sx={{
                        fontFamily: themestyle.fontFamily.Poppins,
                        fontSize: isMobile ? "0.875rem" : "1rem",
                      }}
                    >
                      Click to{" "}
                      <b style={{ color: themestyle.colors.activeGreen }}>
                        {" "}
                        {CLASS_IMAGE_TEXT.UPLOAD}
                      </b>
                    </Typography>
                    <Typography
                      fontSize={isMobile ? "10px" : "12px"}
                      color="text.secondary"
                    >
                      {CLASS_IMAGE_TEXT.FORMAT_INFO}
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
          {errors.coverImage && (
            <Typography
              color="error"
              sx={{
                mt: 0.5,
                display: "block",
                ml: 1.5,
                fontSize: { xs: "10px", sm: "12px" },
                fontFamily: themestyle.fontFamily.Roboto,
              }}
            >
              {errors.coverImage}
            </Typography>
          )}
        </Box>
        <Box>
          <FormLabel>{CLASS_LABELS.DESCRIPTION} *</FormLabel>
          <TextField
            name="description"
            fullWidth
            multiline
            value={formData.description}
            onChange={handleChange}
            rows={3}
            placeholder={CLASS_PLACEHOLDERS.DESCRIPTION}
            sx={textFieldSX}
            disabled={isViewMode}
            error={!!errors.description}
            helperText={errors.description}
          />
        </Box>
      </Box>
      <Box
        sx={{
          px: isMobile ? 1 : { sm: 3, md: 5 },
          pb: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: isMobile ? 1 : 2,
        }}
      >
        {userRole !== "INSTRUCTOR" && (
          <>
            <Button
              variant="outlined"
              onClick={() => onCancel && onCancel()}
              sx={{
                borderColor: themestyle.colors.buttonGreen,
                color: themestyle.colors.black,
                textTransform: "none",
                fontWeight: 600,
                px: isMobile ? 1.5 : 2,
                borderRadius: "8px",
                height: isMobile ? "36px" : "44px",
                fontSize: isMobile ? "13px" : "16px",
                minWidth: isMobile ? "75px" : "110px",
              }}
            >
              {CLASS_BUTTONS.CANCEL}
            </Button>
            {isViewMode && onEdit ? (
              <Button
                variant="contained"
                onClick={onEdit}
                sx={{
                  backgroundColor: themestyle.colors.buttonGreen,
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  px: isMobile ? 1.5 : 5,
                  height: isMobile ? "36px" : "44px",
                  fontSize: isMobile ? "13px" : "16px",
                  minWidth: isMobile ? "100px" : "110px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: themestyle.colors.buttonGreenHover,
                  },
                }}
              >
                {CLASS_BUTTONS.EDIT}
              </Button>
            ) : (
              !isViewMode && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !isFormValid()}
                    sx={{
                      backgroundColor: themestyle.colors.buttonGreen,
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      px: isMobile ? 1.5 : 5,
                      height: isMobile ? "36px" : "44px",
                      fontSize: isMobile ? "13px" : "16px",
                      minWidth: isMobile ? "80px" : "110px",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: themestyle.colors.buttonGreenHover,
                      },
                    }}
                  >
                    {mode === "add"
                      ? CLASS_BUTTONS.ADD
                      : isMobile
                        ? "Update"
                        : CLASS_BUTTONS.UPDATE}
                  </Button>
                </>
              )
            )}
          </>
        )}
      </Box>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={handleSnackbarClose}
      />
      <Backdrop
        sx={{
          color: themestyle.colors.white,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isSaving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};
export default AddClassForm;
